// What makes this app miami-todo.com rather than another *-todo site — the
// counterpart of apps/dubai/src/site.ts. @todo/core reads it as the `@site`
// alias (tsconfig.json). Dependency-free: imported from anywhere.

import type { SiteConfig } from '@todo/core/site-config';

export const SITE = {
  // Short machine name (the Worker's): prefixes this site's browser storage keys.
  id: 'miami-todo',

  // Absolute origin baked into canonical / og:url tags. Override
  // per-environment with EXPO_PUBLIC_SITE_URL if needed.
  url: (process.env.EXPO_PUBLIC_SITE_URL ?? 'https://miami-todo.com').replace(/\/+$/, ''),
  name: 'Miami To-Do',

  // UI languages, in switcher order. Each is a URL prefix (/en, /es, /ru).
  locales: ['en', 'es', 'ru'],
  defaultLocale: 'en',
  // An American site: US English dates and numbers ("Thursday, October 15,
  // 2026", 12-hour clock) rather than the shared en-GB.
  intlLocales: { en: 'en-US' },

  timeZone: 'America/New_York',
  // The feeds' price currency: sync-viator is to be scheduled with
  // `currency: 'USD'` in its cron body (stage 4 of docs/miami-plan.md).
  currency: 'USD',

  // `cities.id` values the site covers (supabase/sites/miami.sql, stage 4).
  // One city: the neighbourhoods (South Beach, Wynwood…) are to be
  // collections, as Dubai's areas are.
  cityIds: ['miami'],

  // Top-level sections, in header order. Each is a /[lang]/<section> route.
  sections: ['tours'],
  // Tour pages list Viator's product facts (included, pickup, know before you
  // go) — the project has the viator_product_details feature (supabase/sites/miami.sql).
  tourDetails: true,

  // ISO 3166 country of the suppliers' region (structured data).
  country: 'US',

  regionName: {
    en: 'Miami',
    es: 'Miami',
    ru: 'Майами',
  },
  description: {
    en: 'Things to do in Miami — tours, Biscayne Bay boat trips, Everglades airboats, Art Deco walks and day trips to Key West.',
    es: 'Qué hacer en Miami: tours, paseos en barco por la bahía de Biscayne, hidrodeslizadores en los Everglades, rutas art déco y excursiones de un día a Key West.',
    ru: 'Чем заняться в Майами — экскурсии, прогулки на катере по заливу Бискейн, аэроглиссеры в Эверглейдс, квартал ар-деко в Саут-Бич и поездки на день в Ки-Уэст.',
  },
  // Breadcrumb labels for the JSON-LD built outside React: keep in sync with
  // `nav.*` in src/lib/i18n/resources/*.json.
  crumbs: {
    tours: { en: 'Tours', es: 'Tours', ru: 'Экскурсии' },
    boats: { en: 'Yachts & Boats', es: 'Yates y barcos', ru: 'Яхты и лодки' },
    articles: { en: 'Guides', es: 'Guías', ru: 'Путеводитель' },
  },
} as const satisfies SiteConfig;

export type SiteLocale = (typeof SITE.locales)[number];
